import React, { useState, useEffect, useCallback } from 'react';
import LoadingProgress from '../components/LoadingProgress';

function fmtTime(sec) {
  if (!sec || sec <= 0) return '--:--.---';
  const min = Math.floor(sec / 60);
  const s = sec % 60;
  return min + ':' + s.toFixed(3).padStart(6, '0');
}

const classBadgeColor = {
  SRF: { bg: 'rgba(39,174,96,0.15)', color: '#27ae60' },
  GT3: { bg: 'rgba(231,76,60,0.15)', color: '#e74c3c' },
  LMP2: { bg: 'rgba(155,89,182,0.15)', color: '#9b59b6' },
  'MX-5': { bg: 'rgba(243,156,18,0.15)', color: '#f39c12' },
  GTE: { bg: 'rgba(46,134,193,0.15)', color: '#2e86c1' },
};

export default function LeaderboardPage({ onLogin }) {
  const [trackIndex, setTrackIndex] = useState([]);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('records'); // records | track

  const loadIndex = useCallback(async () => {
    try {
      const res = await fetch('/api/leaderboard');
      const data = await res.json();
      setTrackIndex(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load leaderboard index:', err);
    }
    setLoading(false);
  }, []);

  const loadTrack = useCallback(async (trackId) => {
    try {
      const res = await fetch(`/api/leaderboard/${trackId}?limit=100`);
      const data = await res.json();
      setLeaderboard(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load track leaderboard:', err);
    }
  }, []);

  useEffect(() => { loadIndex(); }, [loadIndex]);

  useEffect(() => {
    if (selectedTrack) loadTrack(selectedTrack);
  }, [selectedTrack, loadTrack]);

  // Auto-refresh every 30s
  useEffect(() => {
    const interval = setInterval(() => {
      loadIndex();
      if (selectedTrack) loadTrack(selectedTrack);
    }, 30000);
    return () => clearInterval(interval);
  }, [selectedTrack, loadIndex, loadTrack]);

  const selectTrack = (trackId) => {
    setSelectedTrack(trackId);
    setView('track');
  };

  const selectedTrackInfo = trackIndex.find(t => t.track_id === selectedTrack);
  const totalLaps = trackIndex.reduce((sum, t) => sum + (t.total_laps || 0), 0);
  const totalDrivers = trackIndex.reduce((sum, t) => sum + (t.driver_count || 0), 0);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-bg)' }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '12px 24px', background: 'var(--color-surface)', borderBottom: '1px solid var(--color-border)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 36, height: 36, background: 'linear-gradient(135deg,rgba(26,82,118,0.3),rgba(46,134,193,0.1))',
            borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
            filter: 'drop-shadow(0 0 20px rgba(46,134,193,0.3))',
          }}>&#127937;</div>
          <span style={{ fontFamily: 'var(--font-heading)', fontSize: 20, fontWeight: 700, letterSpacing: 2, color: 'var(--color-secondary)' }}>RACEVOICE</span>
          <div style={{ display: 'flex', gap: 8, marginLeft: 16 }}>
            <NavBtn label="Track Records" active={view === 'records'} onClick={() => setView('records')} />
            <NavBtn label="Leaderboard" active={view === 'track'} onClick={() => { if (selectedTrack) setView('track'); }} />
          </div>
        </div>
        <button onClick={onLogin} style={{
          padding: '8px 20px', background: 'linear-gradient(135deg,#1a5276,#2e86c1)', border: 'none',
          borderRadius: 8, color: '#fff', fontFamily: 'var(--font-heading)', fontSize: 11, fontWeight: 700,
          letterSpacing: 2, textTransform: 'uppercase', cursor: 'pointer', boxShadow: '0 4px 15px rgba(26,82,118,0.3)',
        }}>Sign In</button>
      </div>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 24px' }}>

        {/* ── TRACK RECORDS VIEW ── */}
        {view === 'records' && (
          <>
            <div style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 700, color: 'var(--color-secondary)', letterSpacing: 2, marginBottom: 24 }}>
              TRACK RECORDS
            </div>

            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
              <StatCard label="Tracks" value={trackIndex.length} accent />
              <StatCard label="Total Laps" value={totalLaps} />
              <StatCard label="Drivers" value={totalDrivers} />
            </div>

            {loading ? (
              <LoadingProgress label="Loading leaderboard" />
            ) : trackIndex.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 48, color: 'var(--color-text-dim)' }}>No lap records yet.</div>
            ) : (
              <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 10, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Track', 'Record Holder', 'Car', 'Record Lap', 'Laps', 'Drivers', 'Set On'].map(h => (
                        <th key={h} style={{
                          fontFamily: 'var(--font-heading)', fontSize: 10, fontWeight: 700, letterSpacing: 1.5,
                          textTransform: 'uppercase', color: 'var(--color-text-dim)', padding: '12px 14px',
                          background: 'var(--color-surface-light)', borderBottom: '1px solid var(--color-border)', textAlign: 'left',
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {trackIndex.map(track => (
                      <tr key={track.track_id} onClick={() => selectTrack(track.track_id)}
                          style={{ cursor: 'pointer', transition: 'background 0.15s' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(46,134,193,0.04)'}
                          onMouseLeave={e => e.currentTarget.style.background = ''}>
                        <td style={{ padding: '12px 14px', borderBottom: '1px solid var(--color-border)' }}>
                          <span style={{ fontFamily: 'var(--font-heading)', fontSize: 12, fontWeight: 700, letterSpacing: 1 }}>{track.track_name || track.track_id}</span>
                        </td>
                        <td style={{ padding: '12px 14px', borderBottom: '1px solid var(--color-border)', fontWeight: 600, fontSize: 13 }}>{track.best_driver || '--'}</td>
                        <td style={{ padding: '12px 14px', borderBottom: '1px solid var(--color-border)' }}>
                          {track.best_car && <span style={{ display: 'inline-block', padding: '3px 10px', background: 'rgba(46,134,193,0.12)', borderRadius: 12, fontSize: 11, fontWeight: 600, color: 'var(--color-accent)' }}>{track.best_car}</span>}
                        </td>
                        <td style={{ padding: '12px 14px', borderBottom: '1px solid var(--color-border)', fontFamily: 'var(--font-data)', fontSize: 14, fontWeight: 500, color: 'var(--color-success)' }}>{fmtTime(track.best_time)}</td>
                        <td style={{ padding: '12px 14px', borderBottom: '1px solid var(--color-border)', fontFamily: 'var(--font-data)', fontSize: 13, textAlign: 'center' }}>{track.total_laps}</td>
                        <td style={{ padding: '12px 14px', borderBottom: '1px solid var(--color-border)', fontFamily: 'var(--font-data)', fontSize: 13, textAlign: 'center' }}>{track.driver_count}</td>
                        <td style={{ padding: '12px 14px', borderBottom: '1px solid var(--color-border)', fontSize: 12, color: 'var(--color-text-dim)' }}>{track.best_date || '--'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* ── TRACK LEADERBOARD VIEW ── */}
        {view === 'track' && selectedTrackInfo && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
              <button onClick={() => setView('records')} style={{
                padding: '6px 12px', border: '1px solid var(--color-border)', borderRadius: 4,
                background: 'transparent', color: 'var(--color-text-dim)', fontSize: 13, cursor: 'pointer',
              }}>Back</button>
              <span style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 700, color: 'var(--color-secondary)', letterSpacing: 2 }}>
                {selectedTrackInfo.track_name}
              </span>
              <span style={{ fontSize: 12, color: 'var(--color-text-dim)' }}>
                {selectedTrackInfo.total_laps} laps &middot; {selectedTrackInfo.driver_count} drivers
              </span>
            </div>

            {/* Track selector pills */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
              {trackIndex.map(t => (
                <button key={t.track_id} onClick={() => setSelectedTrack(t.track_id)}
                  style={{
                    padding: '6px 14px', borderRadius: 6, fontSize: 11, fontFamily: 'var(--font-heading)',
                    fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', cursor: 'pointer',
                    background: t.track_id === selectedTrack ? 'var(--color-primary)' : 'var(--color-surface)',
                    border: `1px solid ${t.track_id === selectedTrack ? 'var(--color-primary)' : 'var(--color-border)'}`,
                    color: t.track_id === selectedTrack ? '#fff' : 'var(--color-text-dim)',
                  }}>
                  {t.track_name}
                </button>
              ))}
            </div>

            <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 10, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Pos', 'Driver', 'Car', 'Class', 'Best Lap', 'Delta', 'Laps', 'Source'].map(h => (
                      <th key={h} style={{
                        fontFamily: 'var(--font-heading)', fontSize: 10, fontWeight: 700, letterSpacing: 1.5,
                        textTransform: 'uppercase', color: 'var(--color-text-dim)', padding: '12px 14px',
                        background: 'var(--color-surface-light)', borderBottom: '1px solid var(--color-border)',
                        textAlign: h === 'Pos' ? 'center' : 'left',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.length === 0 && (
                    <tr><td colSpan={8} style={{ padding: 32, textAlign: 'center', color: 'var(--color-text-dim)' }}>No laps recorded for this track.</td></tr>
                  )}
                  {leaderboard.map((entry, i) => {
                    const posColor = i === 0 ? '#ffd700' : i === 1 ? '#c0c0c0' : i === 2 ? '#cd7f32' : 'var(--color-text-dim)';
                    const leaderTime = leaderboard[0]?.best_time || 0;
                    const delta = i > 0 && entry.best_time && leaderTime ? entry.best_time - leaderTime : 0;
                    const cc = classBadgeColor[entry.car_class] || { bg: 'rgba(46,134,193,0.12)', color: 'var(--color-accent)' };
                    return (
                      <tr key={`${entry.display_name}-${i}`}>
                        <td style={{ textAlign: 'center', fontFamily: 'var(--font-data)', fontSize: 14, fontWeight: 600, color: posColor, padding: '12px 14px', borderBottom: '1px solid var(--color-border)' }}>{i + 1}</td>
                        <td style={{ padding: '12px 14px', borderBottom: '1px solid var(--color-border)', fontWeight: 600, fontSize: 13 }}>{entry.display_name || entry.user_email}</td>
                        <td style={{ padding: '12px 14px', borderBottom: '1px solid var(--color-border)' }}>
                          <span style={{ display: 'inline-block', padding: '3px 10px', background: 'rgba(46,134,193,0.12)', borderRadius: 12, fontSize: 11, fontWeight: 600, color: 'var(--color-accent)' }}>{entry.car}</span>
                        </td>
                        <td style={{ padding: '12px 14px', borderBottom: '1px solid var(--color-border)' }}>
                          {entry.car_class && <span style={{ display: 'inline-block', padding: '3px 10px', background: cc.bg, borderRadius: 12, fontSize: 11, fontWeight: 600, color: cc.color }}>{entry.car_class}</span>}
                        </td>
                        <td style={{ padding: '12px 14px', borderBottom: '1px solid var(--color-border)', fontFamily: 'var(--font-data)', fontSize: 14, fontWeight: 500, color: 'var(--color-success)' }}>{fmtTime(entry.best_time)}</td>
                        <td style={{ padding: '12px 14px', borderBottom: '1px solid var(--color-border)', fontFamily: 'var(--font-data)', fontSize: 12, color: delta > 0 ? 'var(--color-danger)' : 'var(--color-text-dim)' }}>
                          {delta > 0 ? '+' + delta.toFixed(3) : '--'}
                        </td>
                        <td style={{ padding: '12px 14px', borderBottom: '1px solid var(--color-border)', fontFamily: 'var(--font-data)', fontSize: 13 }}>{entry.total_laps}</td>
                        <td style={{ padding: '12px 14px', borderBottom: '1px solid var(--color-border)' }}>
                          <span style={{ display: 'inline-block', padding: '2px 8px', background: 'rgba(46,134,193,0.08)', border: '1px solid rgba(46,134,193,0.2)', borderRadius: 4, fontFamily: 'var(--font-data)', fontSize: 10, color: 'var(--color-text-dim)', textTransform: 'uppercase' }}>{entry.device_type}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <div style={{ textAlign: 'center', padding: 32, borderTop: '1px solid var(--color-border)', marginTop: 48 }}>
        <div style={{ fontFamily: 'var(--font-heading)', fontSize: 11, letterSpacing: 2, color: 'var(--color-secondary)' }}>RACEVOICE</div>
        <div style={{ fontSize: 12, color: 'var(--color-text-dim)', marginTop: 4 }}>Live Timing & Leaderboards</div>
      </div>
    </div>
  );
}

function NavBtn({ label, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: '6px 16px', border: `1px solid ${active ? 'var(--color-primary)' : 'var(--color-border)'}`,
      borderRadius: 4, background: active ? 'var(--color-primary)' : 'transparent',
      color: active ? '#fff' : 'var(--color-text-dim)', fontFamily: 'var(--font-body)', fontSize: 13, cursor: 'pointer',
    }}>{label}</button>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8, padding: '14px 18px' }}>
      <div style={{ fontFamily: 'var(--font-heading)', fontSize: 10, fontWeight: 500, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--color-text-dim)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontFamily: 'var(--font-data)', fontSize: 22, fontWeight: 500, color: accent ? 'var(--color-accent)' : 'var(--color-text)' }}>{value}</div>
    </div>
  );
}
